/**
 * APP VERSION: 2.0.0 | DATE: 2026-03-19 | FILE: ShippingAutomation.jsx
 * ==========================================================================
 * SHIPPING AUTOMATION UI — Three tabs: Orders, Analytics, Settings
 *
 * CRITICAL BEHAVIORS (DO NOT CHANGE):
 * 1. On mount, polls GET /api/shipping/run-status to detect if automation is
 *    already running (survives page refresh). Button stays grey + shows progress.
 * 2. POST /api/shipping/run returns 409 if already running (backend lock).
 * 3. Progress bar polls /run-status every 3s for step name + percentage.
 * 4. RTO% column shows exact Engage data (engage/engage-prediction/api-fallback).
 * 5. Download Labels/Manifest per-run (not per-date).
 * ==========================================================================
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Play, History, Settings, Shield, ChevronDown, ChevronUp,
  CheckCircle, XCircle, AlertTriangle, Clock,
  Loader2, Save, RefreshCcw, Truck, Download, FileText,
  Package, Calendar, Copy,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildDateButtons() {
  const btns = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const value = d.toISOString().split("T")[0];
    let label;
    if (i === 0) label = "Today";
    else if (i === 1) label = "Yesterday";
    else label = d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    btns.push({ label, value });
  }
  return btns;
}

// =================== SINGLE RUN CARD ===================
function RunCard({ run, idx }) {
  const [expanded, setExpanded] = useState(false);
  const [downloadingLabels, setDownloadingLabels] = useState(false);
  const [downloadingManifest, setDownloadingManifest] = useState(false);
  const hasShipped = run.shipped > 0;

  const downloadLabels = async () => {
    setDownloadingLabels(true);
    try {
      const res = await axios.post(`${API}/shipping/labels-by-run`, { started_at: run.started_at }, { timeout: 60000 });
      if (res.data?.label_url) {
        window.open(res.data.label_url, "_blank");
        toast.success(`Labels generated for ${res.data.count} shipment(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate labels");
    } finally { setDownloadingLabels(false); }
  };

  const downloadManifest = async () => {
    setDownloadingManifest(true);
    try {
      const res = await axios.post(`${API}/shipping/manifest-by-run`, { started_at: run.started_at }, { timeout: 60000 });
      if (res.data?.manifest_url) {
        window.open(res.data.manifest_url, "_blank");
        toast.success(`Manifest generated for ${res.data.count} shipment(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate manifest");
    } finally { setDownloadingManifest(false); }
  };

  return (
    <Card data-testid={`run-card-${idx}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Run at {new Date(run.started_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={run.status === "completed" ? "default" : "destructive"} className={run.status === "completed" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}>
              {run.status}
            </Badge>
            <span className="text-xs text-slate-500">{new Date(run.started_at).toLocaleDateString("en-IN")}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-5 gap-3 text-center mb-4">
          {[
            { val: run.total_orders, label: "Total", bg: "bg-slate-50", color: "text-slate-900" },
            { val: run.approved, label: "Approved", bg: "bg-emerald-50", color: "text-emerald-600" },
            { val: run.rejected, label: "Rejected", bg: "bg-red-50", color: "text-red-500" },
            { val: run.shipped, label: "Shipped", bg: "bg-blue-50", color: "text-blue-600" },
            { val: run.errors, label: "Errors", bg: "bg-amber-50", color: "text-amber-500" },
          ].map((s) => (
            <div key={s.label} className={`p-2.5 ${s.bg} rounded-lg`}>
              <div className={`text-xl font-bold ${s.color}`}>{s.val ?? 0}</div>
              <div className="text-[11px] text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {hasShipped && (
          <div className="flex gap-3 mb-3">
            <Button variant="outline" size="sm" onClick={downloadLabels} disabled={downloadingLabels} data-testid={`download-labels-${idx}`}>
              {downloadingLabels ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Labels
            </Button>
            <Button variant="outline" size="sm" onClick={downloadManifest} disabled={downloadingManifest} data-testid={`download-manifest-${idx}`}>
              {downloadingManifest ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
              Manifest
            </Button>
          </div>
        )}

        {run.status === "failed" && run.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-sm text-red-700">
            <span className="font-medium">Error: </span>{run.error_detail || run.error}
          </div>
        )}

        {run.status === "completed" && run.total_orders === 0 && run.message && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3 text-sm text-slate-600">
            {run.message}
          </div>
        )}

        {run.orders?.length > 0 && (
          <div>
            <button className="text-sm text-slate-600 flex items-center gap-1 hover:text-slate-900 mb-2" onClick={() => setExpanded(!expanded)} data-testid={`toggle-details-${idx}`}>
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Order details ({run.orders.length})
            </button>
            {expanded && (
              <div className="max-h-72 overflow-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Order</th>
                      <th className="text-left p-2 font-medium">Customer</th>
                      <th className="text-right p-2 font-medium">Value</th>
                      <th className="text-center p-2 font-medium">Buyer RTO%</th>
                      <th className="text-center p-2 font-medium">Threshold</th>
                      <th className="text-center p-2 font-medium">Decision</th>
                      <th className="text-left p-2 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.orders.map((o, i) => (
                      <tr key={i} className={`border-b border-slate-100 ${o.approved ? "" : "bg-red-50/30"}`}>
                        <td className="p-2 font-mono text-xs">{o.channel_order_id || o.order_id}</td>
                        <td className="p-2">{o.customer_name}</td>
                        <td className="p-2 text-right font-mono">Rs. {parseFloat(o.total || 0).toLocaleString()}</td>
                        <td className="p-2 text-center">
                          <div className="flex flex-col items-center">
                            <span className={`font-mono font-semibold ${o.rto_pct <= 15 ? "text-emerald-600" : o.rto_pct <= 35 ? "text-amber-600" : "text-red-600"}`}>
                              {o.rto_pct}%
                            </span>
                            {o.buyer_info && <span className="text-[10px] text-slate-400">{o.buyer_info}</span>}
                            {o.rto_source === "api-fallback" && <span className="text-[10px] text-orange-400">~approx</span>}
                          </div>
                        </td>
                        <td className="p-2 text-center text-slate-500">{o.threshold}%</td>
                        <td className="p-2 text-center">
                          {o.approved ? <CheckCircle className="w-4 h-4 text-emerald-500 inline" /> : <XCircle className="w-4 h-4 text-red-500 inline" />}
                        </td>
                        <td className="p-2 text-slate-600 max-w-44 truncate">{o.ship_result || o.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =================== ORDERS TAB ===================
function OrdersTab({ running, setRunning, rules, onRunComplete }) {
  const [pendingCount, setPendingCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [pendingDays, setPendingDays] = useState(30);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [runsForDate, setRunsForDate] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [progressStep, setProgressStep] = useState("");
  const [progressPct, setProgressPct] = useState(0);

  const dateButtons = useMemo(() => buildDateButtons(), []);

  const fetchPendingCount = useCallback(async () => {
    setLoadingCount(true);
    let retries = 2;
    while (retries >= 0) {
      try {
        const res = await axios.get(`${API}/shipping/pending-count`, { params: { days: pendingDays }, timeout: 30000 });
        setPendingCount(res.data?.count ?? 0);
        setLoadingCount(false);
        return;
      } catch (e) {
        if (retries === 0) {
          console.error("Pending count failed after retries:", e.response?.data?.detail || e.message);
          setPendingCount(null);
        }
        retries--;
        if (retries >= 0) await new Promise(r => setTimeout(r, 2000));
      }
    }
    setLoadingCount(false);
  }, [pendingDays]);

  const fetchRunsForDate = useCallback(async (date) => {
    setLoadingRuns(true);
    try {
      const res = await axios.get(`${API}/shipping/runs`, { params: { date, limit: 50 } });
      setRunsForDate(res.data || []);
    } catch { setRunsForDate([]); }
    finally { setLoadingRuns(false); }
  }, []);

  // On mount: check if automation is already running (handles page refresh)
  useEffect(() => {
    const checkRunning = async () => {
      try {
        const res = await axios.get(`${API}/shipping/run-status`);
        if (res.data?.running) {
          setRunning(true);
          setProgressStep(res.data.step || "Processing...");
          setProgressPct(res.data.progress || 10);
        }
      } catch {}
    };
    checkRunning();
    fetchPendingCount();
  }, [fetchPendingCount, setRunning]);

  useEffect(() => { fetchRunsForDate(selectedDate); }, [selectedDate, fetchRunsForDate]);

  const runAutomation = async () => {
    setRunning(true);
    setProgressStep("Starting automation...");
    setProgressPct(5);
    try {
      await axios.post(`${API}/shipping/run`, rules, { timeout: 30000 });
      toast.success("Automation started!");

      // Poll both status (for progress) and latest run (for completion)
      const poll = setInterval(async () => {
        try {
          // Check real-time progress
          const statusRes = await axios.get(`${API}/shipping/run-status`);
          const status = statusRes.data;
          if (status?.running) {
            setProgressStep(status.step || "Processing...");
            setProgressPct(status.progress || 10);
          } else {
            // Automation finished — check result
            const runRes = await axios.get(`${API}/shipping/runs/latest`);
            const run = runRes.data;
            clearInterval(poll);
            setRunning(false);
            setProgressStep("");
            setProgressPct(0);
            fetchPendingCount();
            fetchRunsForDate(selectedDate);
            onRunComplete?.();
            if (run?.status === "completed") {
              toast.success(`Done: ${run.approved} approved, ${run.rejected} rejected, ${run.shipped} shipped`);
            } else if (run?.status === "failed") {
              toast.error(`Failed: ${run.error || "Unknown error"}`);
            }
          }
        } catch { /* keep polling */ }
      }, 3000);

      // Safety timeout: 5 minutes max
      setTimeout(() => {
        clearInterval(poll);
        setRunning(false);
        setProgressStep("");
        setProgressPct(0);
        fetchRunsForDate(selectedDate);
      }, 300000);

    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to start";
      if (e.response?.status === 409) {
        toast.error("Automation is already running. Please wait for it to finish.");
      } else {
        toast.error(msg);
      }
      // Re-check if automation is actually running
      try {
        const res = await axios.get(`${API}/shipping/run-status`);
        if (res.data?.running) {
          setProgressStep(res.data.step || "Processing...");
          setProgressPct(res.data.progress || 10);
        } else {
          setRunning(false);
        }
      } catch {
        setRunning(false);
      }
    }
  };

  return (
    <div className="space-y-5">
      {/* Pending Orders + Run */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-semibold text-lg text-slate-900">Pending Orders</h2>
                <div className="flex items-center gap-3 mt-0.5">
                  {loadingCount ? (
                    <span className="text-sm text-slate-500">Loading...</span>
                  ) : pendingCount !== null ? (
                    <span className="text-sm text-slate-600">
                      <span className="text-2xl font-bold text-slate-900 mr-1">{pendingCount}</span>
                      new orders
                    </span>
                  ) : (
                    <span className="text-sm text-red-500 cursor-pointer hover:underline" onClick={fetchPendingCount}>Unable to fetch — tap to retry</span>
                  )}
                  <select
                    value={pendingDays}
                    onChange={(e) => { setPendingDays(Number(e.target.value)); setPendingCount(null); }}
                    className="text-xs border rounded px-1.5 py-0.5 bg-white text-slate-600"
                    data-testid="pending-days-filter"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>All time</option>
                  </select>
                  <button onClick={fetchPendingCount} className="text-slate-400 hover:text-slate-600" data-testid="refresh-pending-count">
                    <RefreshCcw className={`w-3.5 h-3.5 ${loadingCount ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>
            </div>
            <Button size="lg" onClick={runAutomation} disabled={running || pendingCount === 0}
              className={running ? "bg-slate-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"} data-testid="run-automation-btn">
              {running
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</>
                : <><Play className="w-4 h-4 mr-2" />Run Automation</>}
            </Button>
          </div>
          {running && (
            <div className="mt-4 space-y-2" data-testid="automation-progress">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
                  <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
                  <span className="font-medium">{progressStep || "Processing..."}</span>
                </div>
                <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progressPct}%` }} data-testid="progress-bar" />
                </div>
                <p className="text-xs text-blue-500 mt-1.5">Do not close this page. The automation is running in the background.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <Calendar className="w-4 h-4 text-slate-400" />
        {dateButtons.map((btn) => (
          <button key={btn.value} onClick={() => setSelectedDate(btn.value)}
            className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
              selectedDate === btn.value
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`date-btn-${btn.value}`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Runs for selected date */}
      {loadingRuns ? (
        <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading runs...
        </div>
      ) : runsForDate.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500 text-sm">
            No automation runs on {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {runsForDate.map((run, idx) => (
            <RunCard key={run.started_at} run={run} idx={idx} />
          ))}
        </div>
      )}
    </div>
  );
}

// =================== ANALYTICS TAB ===================
// =================== ENGAGE TOKEN BANNER ===================
function EngageTokenBanner() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/engage/token-status`)
      .then(r => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const bookmarklet = `javascript:void(fetch("${process.env.REACT_APP_BACKEND_URL}/api/engage/token",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({token:localStorage.getItem("satellizer_token")})}).then(r=>r.json()).then(d=>d.success?alert("Token saved successfully! You can close this tab."):alert("Error: "+(d.detail||"Unknown error"))).catch(()=>alert("Failed to connect")))`;

  const copyBookmarklet = () => {
    navigator.clipboard.writeText(bookmarklet);
    toast.success("Copied! Paste into Shiprocket tab's address bar");
  };

  // Token is active
  if (status?.has_token && status?.is_valid) {
    const savedAt = status.saved_at ? new Date(status.saved_at) : null;
    const daysElapsed = savedAt ? Math.floor((Date.now() - savedAt.getTime()) / 86400000) : 0;
    const daysRemaining = Math.max(10 - daysElapsed, 0);

    return (
      <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs ${
        daysRemaining <= 2 ? "bg-amber-50 border border-amber-200 text-amber-700" : "bg-emerald-50 border border-emerald-200 text-emerald-700"
      }`} data-testid="engage-banner">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${daysRemaining <= 2 ? "bg-amber-500" : "bg-emerald-500"}`}></div>
          <span className="font-medium">Engage RTO: Active</span>
          <span className="text-[11px] opacity-75">
            {daysRemaining <= 2
              ? `Expires soon (${daysRemaining}d left) — refresh from Shiprocket`
              : `~${daysRemaining}d remaining`
            }
          </span>
        </div>
        {daysRemaining <= 2 && (
          <button onClick={copyBookmarklet} className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 hover:bg-amber-200 rounded text-[11px] font-medium transition-colors" data-testid="engage-banner-copy">
            <Copy className="w-3 h-3" /> Copy refresh code
          </button>
        )}
      </div>
    );
  }

  // Token missing or expired
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg text-xs bg-red-50 border border-red-200 text-red-700" data-testid="engage-banner">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5" />
        <span className="font-medium">Engage RTO: {status?.has_token ? "Expired" : "Not connected"}</span>
        <span className="text-[11px] opacity-75">RTO% data will be inaccurate</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] opacity-75">Log into Shiprocket, then</span>
        <button onClick={copyBookmarklet} className="flex items-center gap-1 px-2 py-1 bg-red-100 hover:bg-red-200 rounded text-[11px] font-medium transition-colors" data-testid="engage-banner-copy">
          <Copy className="w-3 h-3" /> Copy connect code
        </button>
      </div>
    </div>
  );
}

// =================== SETTINGS TAB ===================
function SettingsTab({ rules, setRules, loadAll, runHistory }) {
  const [savingRules, setSavingRules] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false, time: "10:00", days: [] });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [connStatus, setConnStatus] = useState(null);

  const DAYS = [
    { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" }, { key: "wed", label: "Wed" },
    { key: "thu", label: "Thu" }, { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" }, { key: "sun", label: "Sun" },
  ];

  useEffect(() => {
    (async () => {
      try {
        const [credsRes, schedRes] = await Promise.all([
          axios.get(`${API}/shipping/credentials`),
          axios.get(`${API}/shipping/schedule`),
        ]);
        setConnStatus({ email: credsRes.data?.email, connected: credsRes.data?.has_password });
        if (schedRes.data?.time) setSchedule(schedRes.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const saveRules = async () => {
    setSavingRules(true);
    try { await axios.put(`${API}/shipping/rules`, rules); toast.success("Rules saved"); }
    catch { toast.error("Failed to save rules"); }
    finally { setSavingRules(false); }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try { await axios.put(`${API}/shipping/schedule`, schedule); toast.success("Schedule saved"); }
    catch { toast.error("Failed to save"); }
    finally { setSavingSchedule(false); }
  };

  const toggleDay = (day) => {
    setSchedule(prev => ({ ...prev, days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day] }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Decision Rules */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-sm flex items-center justify-center"><Settings className="w-4.5 h-4.5 text-blue-600" /></div>
            <div><CardTitle className="text-base">Decision Rules</CardTitle><CardDescription>RTO thresholds for order approval</CardDescription></div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Standard Orders — Max RTO %</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" value={rules.default_rto_threshold} onChange={(e) => setRules(r => ({ ...r, default_rto_threshold: parseFloat(e.target.value) }))} className="w-24 h-8" data-testid="standard-rto-input" />
                <span className="text-sm text-slate-500">{"% (reject if >=)"}</span>
              </div>
            </div>
            <Separator />
            <div>
              <Label className="text-xs text-slate-600">High-Value Order Threshold</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-slate-500">Rs.</span>
                <Input type="number" value={rules.high_value_threshold} onChange={(e) => setRules(r => ({ ...r, high_value_threshold: parseFloat(e.target.value) }))} className="w-32 h-8" data-testid="high-value-threshold-input" />
                <span className="text-sm text-slate-500">and above</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">High-Value — Max RTO %</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" value={rules.high_value_rto_threshold} onChange={(e) => setRules(r => ({ ...r, high_value_rto_threshold: parseFloat(e.target.value) }))} className="w-24 h-8" data-testid="high-value-rto-input" />
                <span className="text-sm text-slate-500">% (stricter)</span>
              </div>
            </div>
          </div>
          <Button size="sm" onClick={saveRules} disabled={savingRules} data-testid="save-rules-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" />{savingRules ? "Saving..." : "Save Rules"}
          </Button>
        </CardContent>
      </Card>

      {/* Shiprocket Connection */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 ${connStatus?.connected ? "bg-emerald-100" : "bg-red-100"} rounded-sm flex items-center justify-center`}>
              <Shield className={`w-4.5 h-4.5 ${connStatus?.connected ? "text-emerald-600" : "text-red-600"}`} />
            </div>
            <div>
              <CardTitle className="text-base">Shiprocket Connection</CardTitle>
              <CardDescription>Auto-configured via environment</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {connStatus ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {connStatus.connected
                  ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                  : <XCircle className="w-4 h-4 text-red-500" />}
                <span className="text-sm font-medium">{connStatus.connected ? "Connected" : "Not Connected"}</span>
              </div>
              {connStatus.email && (
                <p className="text-xs text-slate-500">API User: {connStatus.email}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Checking connection...</p>
          )}
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-100 rounded-sm flex items-center justify-center"><Clock className="w-4.5 h-4.5 text-amber-600" /></div>
            <div><CardTitle className="text-base">Schedule</CardTitle><CardDescription>Auto-run at a set time on selected days</CardDescription></div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Enable Auto-Run</Label>
            <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule(s => ({ ...s, enabled: v }))} data-testid="schedule-toggle" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Run Time</Label>
            <Input type="time" value={schedule.time} onChange={(e) => setSchedule(s => ({ ...s, time: e.target.value }))} className="w-36 h-8" data-testid="schedule-time-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Days</Label>
            <div className="flex gap-1.5">
              {DAYS.map(d => (
                <button key={d.key} onClick={() => toggleDay(d.key)} className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${schedule.days.includes(d.key) ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`day-${d.key}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={saveSchedule} disabled={savingSchedule} data-testid="save-schedule-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" />{savingSchedule ? "Saving..." : "Save Schedule"}
          </Button>
        </CardContent>
      </Card>

      {/* Run History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-100 rounded-sm flex items-center justify-center"><History className="w-4.5 h-4.5 text-slate-600" /></div>
              <div><CardTitle className="text-base">Run History</CardTitle><CardDescription>Previous automation runs</CardDescription></div>
            </div>
            <Button variant="ghost" size="sm" onClick={loadAll}><RefreshCcw className="w-3.5 h-3.5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {runHistory.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No runs yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-auto">
              {runHistory.map((run, i) => (
                <div key={i} className="p-2.5 bg-slate-50 rounded-lg border text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{new Date(run.started_at).toLocaleString()}</span>
                    <Badge variant="outline" className={`text-xs ${run.status === "completed" ? "border-emerald-300 text-emerald-700" : "border-red-300 text-red-700"}`}>
                      {run.status}
                    </Badge>
                  </div>
                  <div className="flex gap-4 mt-1.5 text-xs">
                    <span>{run.total_orders} orders</span>
                    <span className="text-emerald-600">{run.approved} approved</span>
                    <span className="text-red-500">{run.rejected} rejected</span>
                    <span className="text-blue-600">{run.shipped} shipped</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =================== MAIN PAGE ===================
export default function ShippingAutomation() {
  const [running, setRunning] = useState(false);
  const [runHistory, setRunHistory] = useState([]);
  const [rules, setRules] = useState({
    default_rto_threshold: 35,
    high_value_threshold: 20000,
    high_value_rto_threshold: 25,
  });

  const loadAll = useCallback(async () => {
    try {
      const [rulesRes, histRes] = await Promise.all([
        axios.get(`${API}/shipping/rules`),
        axios.get(`${API}/shipping/runs?limit=20`),
      ]);
      if (rulesRes.data?.default_rto_threshold) setRules(rulesRes.data);
      setRunHistory(histRes.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-5 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Shipping Automation</h1>
        <p className="text-sm text-slate-500 mt-1">Automate your Shiprocket shipping workflow with RTO-based decision rules</p>
      </div>

      <EngageTokenBanner />

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="grid w-full max-w-xs grid-cols-2">
          <TabsTrigger value="orders" data-testid="tab-orders">
            <Package className="w-3.5 h-3.5 mr-1.5" />Orders
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="w-3.5 h-3.5 mr-1.5" />Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <OrdersTab
            running={running}
            setRunning={setRunning}
            rules={rules}
            onRunComplete={loadAll}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <SettingsTab
            rules={rules}
            setRules={setRules}
            loadAll={loadAll}
            runHistory={runHistory}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
